import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { TelegramChatModel } from '@shared/types';

import { TelegramChatState } from './types';

const initialState: TelegramChatState = {
  initialTelegramChatList: [],
  initialTelegramChatPage: 1,
  initialTelegramChatTotalCount: 0,
  assignedTelegramChatList: [],
  assignedTelegramChatPage: 1,
  assignedTelegramChatTotalCount: 0,
  processingTelegramChat: {} as TelegramChatModel,
};

export const telegramChatSlice = createSlice({
  name: 'telegramChat',
  initialState,
  reducers: {
    setInitialTelegramChatList: (state: TelegramChatState, { payload }: PayloadAction<TelegramChatModel[]>) => {
      state.initialTelegramChatList = payload;
    },
    setInitialTelegramChat: (state: TelegramChatState, { payload }: PayloadAction<TelegramChatModel>) => {
      const copiedArray = [...state.initialTelegramChatList];

      copiedArray.unshift(payload);

      state.initialTelegramChatList = copiedArray;
      state.initialTelegramChatPage = 1;
      state.initialTelegramChatTotalCount += 1;
    },
    setInitialTelegramChatPage: (state: TelegramChatState, { payload }: PayloadAction<number>) => {
      state.initialTelegramChatPage = payload;
    },
    setInitialTelegramChatTotalCount: (state: TelegramChatState, { payload }: PayloadAction<number>) => {
      state.initialTelegramChatTotalCount = payload;
    },
    updateInitialTelegramChat: (state: TelegramChatState, { payload }: PayloadAction<TelegramChatModel>) => {
      const mappedTelegramChatsList = state.initialTelegramChatList.map((telegramChat) => {
        const { id: telegramChatId } = telegramChat;
        const { id: payloadId } = payload;

        if (telegramChatId === payloadId) {
          return payload;
        }

        return telegramChat;
      });

      state.initialTelegramChatList = mappedTelegramChatsList;
    },
    deleteInitialTelegramChat: (state: TelegramChatState, { payload: telegramChatId }: PayloadAction<string>) => {
      const filteredTelegramChatsList = state.initialTelegramChatList.filter((telegramChat) => {
        const { id: currentTelegramChatId } = telegramChat;

        return telegramChatId !== currentTelegramChatId;
      });

      state.initialTelegramChatList = filteredTelegramChatsList;
    },
    setAssignedTelegramChatList: (state: TelegramChatState, { payload }: PayloadAction<TelegramChatModel[]>) => {
      state.assignedTelegramChatList = payload;
    },
    setAssignedTelegramChat: (state: TelegramChatState, { payload }: PayloadAction<TelegramChatModel>) => {
      const copiedArray = [...state.assignedTelegramChatList];

      copiedArray.unshift(payload);

      state.assignedTelegramChatList = copiedArray;
      state.assignedTelegramChatPage = 1;
      state.assignedTelegramChatTotalCount += 1;
    },
    setAssignedTelegramChatPage: (state: TelegramChatState, { payload }: PayloadAction<number>) => {
      state.assignedTelegramChatPage = payload;
    },
    setAssignedTelegramChatTotalCount: (state: TelegramChatState, { payload }: PayloadAction<number>) => {
      state.assignedTelegramChatTotalCount = payload;
    },
    updateAssignedTelegramChat: (state: TelegramChatState, { payload }: PayloadAction<TelegramChatModel>) => {
      const mappedTelegramChatsList = state.assignedTelegramChatList.map((telegramChat) => {
        const { id: telegramChatId } = telegramChat;
        const { id: payloadId } = payload;

        if (telegramChatId === payloadId) {
          return payload;
        }

        return telegramChat;
      });

      state.assignedTelegramChatList = mappedTelegramChatsList;
    },
    setProcessingTelegramChat: (state: TelegramChatState, { payload }: PayloadAction<TelegramChatModel>) => {
      state.processingTelegramChat = payload;
    },
    updateTelegramChatsChatStatus: (
      state: TelegramChatState,
      {
        payload,
      }: PayloadAction<{
        isChatMessagesRead: boolean;
        userId: string;
      }>
    ) => {
      const mappedTelegramChatsList = state.assignedTelegramChatList.map((telegramChat) => {
        const { userId } = telegramChat;
        const { userId: payloadUserId, isChatMessagesRead } = payload;

        if (payloadUserId === userId) {
          return { ...telegramChat, isChatMessagesRead };
        }

        return telegramChat;
      });

      const mappedInitialTelegramChatsList = state.initialTelegramChatList.map((telegramChat) => {
        const { userId } = telegramChat;
        const { userId: payloadUserId, isChatMessagesRead } = payload;

        if (payloadUserId === userId) {
          return { ...telegramChat, isChatMessagesRead };
        }

        return telegramChat;
      });

      state.initialTelegramChatList = mappedInitialTelegramChatsList;
      state.assignedTelegramChatList = mappedTelegramChatsList;
    },
  },
});

export const {
  setInitialTelegramChatList,
  setInitialTelegramChatPage,
  setInitialTelegramChatTotalCount,
  updateInitialTelegramChat,
  deleteInitialTelegramChat,
  setAssignedTelegramChatList,
  setAssignedTelegramChatTotalCount,
  updateTelegramChatsChatStatus,
  setAssignedTelegramChat,
  setAssignedTelegramChatPage,
  setProcessingTelegramChat,
  updateAssignedTelegramChat,
} = telegramChatSlice.actions;

export const telegramChatSliceReducer = telegramChatSlice.reducer;
